import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Cloud,
  Computer,
  Storage,
  AttachMoney,
  TrendingUp,
  Warning,
  CheckCircle,
  Timeline,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const StatCard = ({ title, value, subtitle, icon, color, trend }) => (
  <Card sx={{ 
    height: '100%',
    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
    border: `1px solid ${color}30`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0 8px 25px ${color}25`
    }
  }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color }}>
            {value}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {subtitle}
          </Typography>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingUp sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: '#4caf50' }}>
                {trend}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ 
          backgroundColor: color,
          borderRadius: 2,
          p: 1,
          color: 'white'
        }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);
StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  color: PropTypes.string.isRequired,
  trend: PropTypes.string
};

const ResourceStatusCard = ({ resources }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Resource Status
      </Typography>
      {resources.map((resource, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">{resource.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {resource.used}/{resource.total}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(resource.used / resource.total) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#f5f5f5',
              '& .MuiLinearProgress-bar': {
                backgroundColor: resource.used / resource.total > 0.8 ? '#f44336' : '#4caf50'
              }
            }}
          />
        </Box>
      ))}
    </CardContent>
  </Card>
);
ResourceStatusCard.propTypes = {
  resources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      used: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired
    })
  )
};

const RecentActivityCard = ({ activities }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Recent Activities
      </Typography>
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {activities.map((activity, index) => (
          <Box key={index} sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            p: 1,
            borderRadius: 1,
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}>
            <Box sx={{ 
              backgroundColor: activity.type === 'success' ? '#4caf50' : activity.type === 'warning' ? '#ff9800' : '#2196f3',
              borderRadius: '50%',
              p: 0.5,
              mr: 2,
              color: 'white'
            }}>
              {activity.type === 'success' ? <CheckCircle sx={{ fontSize: 16 }} /> : 
               activity.type === 'warning' ? <Warning sx={{ fontSize: 16 }} /> : 
               <Timeline sx={{ fontSize: 16 }} />}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {activity.title}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {activity.time}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </CardContent>
  </Card>
);
RecentActivityCard.propTypes = {
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['success', 'warning', 'info']).isRequired
    })
  )
};

export const SuperAdminWidgets = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Total Resources"
        value="1,247"
        subtitle="Across all clouds"
        icon={<Cloud />}
        color="#1976d2"
        trend="+12% this month"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Active VMs"
        value="342"
        subtitle="Running instances"
        icon={<Computer />}
        color="#388e3c"
        trend="+5% this week"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Monthly Cost"
        value="$24,567"
        subtitle="Current month"
        icon={<AttachMoney />}
        color="#f57c00"
        trend="-3% vs last month"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Pending Approvals"
        value="23"
        subtitle="Awaiting review"
        icon={<Warning />}
        color="#d32f2f"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <ResourceStatusCard
        resources={[
          { name: 'Azure VMs', used: 85, total: 100 },
          { name: 'AWS EC2', used: 67, total: 100 },
          { name: 'Storage', used: 45, total: 100 },
          { name: 'Network', used: 78, total: 100 }
        ]}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <RecentActivityCard
        activities={[
          { title: 'VM created in Azure West US', time: '2 minutes ago', type: 'success' },
          { title: 'Budget alert: 80% threshold reached', time: '15 minutes ago', type: 'warning' },
          { title: 'New user approved', time: '1 hour ago', type: 'success' },
          { title: 'Resource group deployed', time: '2 hours ago', type: 'success' },
          { title: 'Security scan completed', time: '3 hours ago', type: 'info' }
        ]}
      />
    </Grid>
  </Grid>
);

export const SREWidgets = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="System Health"
        value="99.8%"
        subtitle="Uptime this month"
        icon={<CheckCircle />}
        color="#4caf50"
        trend="Above SLA"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Active Incidents"
        value="2"
        subtitle="P1: 0, P2: 2"
        icon={<Warning />}
        color="#ff9800"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Infrastructure"
        value="156"
        subtitle="Monitored services"
        icon={<Storage />}
        color="#2196f3"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Response Time"
        value="245ms"
        subtitle="Average API latency"
        icon={<Timeline />}
        color="#9c27b0"
        trend="-15ms improved"
      />
    </Grid>
  </Grid>
);

export const FinOpsWidgets = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Monthly Spend"
        value="$18,453"
        subtitle="Current month"
        icon={<AttachMoney />}
        color="#1976d2"
        trend="-8% vs budget"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Cost Savings"
        value="$3,267"
        subtitle="This quarter"
        icon={<TrendingUp />}
        color="#4caf50"
        trend="+23% identified"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Unutilized Resources"
        value="12"
        subtitle="Optimization opportunities"
        icon={<Warning />}
        color="#ff9800"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Budget Variance"
        value="-12%"
        subtitle="Under budget"
        icon={<Timeline />}
        color="#4caf50"
        trend="On track"
      />
    </Grid>
  </Grid>
);

export const AppOwnerWidgets = () => (
  <Grid container spacing={3}>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="My Resources"
        value="23"
        subtitle="Active resources"
        icon={<Cloud />}
        color="#7b1fa2"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Monthly Cost"
        value="$1,245"
        subtitle="My allocation"
        icon={<AttachMoney />}
        color="#1976d2"
        trend="65% of budget used"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Pending Requests"
        value="3"
        subtitle="Awaiting approval"
        icon={<Warning />}
        color="#ff9800"
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <StatCard
        title="Applications"
        value="8"
        subtitle="Deployed apps"
        icon={<Computer />}
        color="#4caf50"
      />
    </Grid>
  </Grid>
);
